/**
 * The active specialty — provider and hook.
 *
 * rev v11.0.0 · Phase 2 / C2
 *
 * The platform had NO specialty context until now: the specialty list was
 * only a set of tabs in the Cotations screen. This provider introduces the
 * concept, and it does so in the least invasive way possible:
 *
 *   • the specialty is read from the URL first (`?specialty=<slug>`), so a
 *     link to a specialty workspace is shareable;
 *   • it falls back to the last specialty used in this browser;
 *   • it falls back, finally, to general medicine.
 *
 * The provider is mounted once, in the root route. It writes the
 * `data-specialty` attribute on the document element so that the CSS
 * variables of the specialty apply to the whole interface — including the
 * 48 shadcn/ui components, which read the same variables and are therefore
 * recoloured without a single component being forked.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  ALL_SPECIALTIES,
  getSpecialty,
  resolveSpecialtyId,
  type SpecialtyDefinition,
} from "@/lib/specialty";
import { specialtyRegistry } from "@dikdoc/specialty-registry";

const STORAGE_KEY = "dikdoc.specialty";
const DEFAULT_SPECIALTY = "medecine-generale";

interface SpecialtyContextValue {
  /** The active specialty. Never null: it falls back to general medicine. */
  specialty: SpecialtyDefinition;
  /** The canonical slug of the active specialty. */
  slug: string;
  /** Every specialty, in clinical order. */
  specialties: readonly SpecialtyDefinition[];
  /** Change the active specialty. */
  setSpecialty: (id: string) => void;
  /** The CSS custom properties of the active specialty. */
  themeStyle: Record<string, string>;
}

const SpecialtyContext = createContext<SpecialtyContextValue | null>(null);

/**
 * The CSS custom properties of a specialty.
 *
 * The registry is the single source of the token set: it declares TEN
 * tokens (primary, primary-foreground, accent, accent-foreground,
 * destructive, success, warning, surface, radius, font-display) and the
 * interface reads all of them. This function used to rebuild the list by
 * hand and silently dropped `success`, `warning` and `surface` — the
 * specialty colours of those states never reached the DOM.
 */
function buildThemeStyle(specialty: SpecialtyDefinition): Record<string, string> {
  return specialtyRegistry.themeStyle(specialty.id);
}

function readInitialSpecialty(): string {
  if (typeof window === "undefined") return DEFAULT_SPECIALTY;
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("specialty");
    const resolved = resolveSpecialtyId(fromUrl);
    if (resolved) return resolved;
  } catch {
    /* a malformed URL must never break the application */
  }
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    const resolved = resolveSpecialtyId(stored);
    if (resolved) return resolved;
  } catch {
    /* localStorage may be unavailable in a private context */
  }
  return DEFAULT_SPECIALTY;
}

export function SpecialtyProvider({ children }: { children: ReactNode }) {
  const [slug, setSlug] = useState<string>(readInitialSpecialty);

  const specialty = useMemo(
    () => getSpecialty(slug) ?? getSpecialty(DEFAULT_SPECIALTY)!,
    [slug],
  );

  const setSpecialty = useCallback((id: string) => {
    const resolved = resolveSpecialtyId(id);
    if (!resolved) return;
    setSlug(resolved);
    try {
      window.localStorage.setItem(STORAGE_KEY, resolved);
    } catch {
      /* not fatal */
    }
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("specialty", resolved);
      // replaceState, not pushState: switching specialty is not a navigation
      // the user wants to go back through.
      window.history.replaceState(window.history.state, "", url.toString());
    } catch {
      /* not fatal */
    }
  }, []);

  // The attribute lives on <html> so that the CSS variables cascade to the
  // whole document, including the portalled overlays.
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-specialty", specialty.id);
    // Every token the registry declares is applied — not a hand-picked
    // subset. A token that is not written here keeps the default colour
    // of the application, which is a silent loss of the specialty identity.
    const style = buildThemeStyle(specialty);
    for (const [token, value] of Object.entries(style)) {
      root.style.setProperty(token, value);
    }
    return () => {
      root.removeAttribute("data-specialty");
    };
  }, [specialty]);

  const value = useMemo<SpecialtyContextValue>(
    () => ({
      specialty,
      slug: specialty.id,
      specialties: ALL_SPECIALTIES,
      setSpecialty,
      themeStyle: buildThemeStyle(specialty),
    }),
    [specialty, setSpecialty],
  );

  return (
    <SpecialtyContext.Provider value={value}>{children}</SpecialtyContext.Provider>
  );
}

/**
 * The active specialty.
 *
 * Throws when used outside the provider: a silent default would hide a
 * wiring mistake until a hospital found it.
 */
export function useSpecialty(): SpecialtyContextValue {
  const value = useContext(SpecialtyContext);
  if (!value) {
    throw new Error(
      "useSpecialty() must be used inside <SpecialtyProvider>. Mount it in the root route.",
    );
  }
  return value;
}

/** The active specialty, or null when there is no provider. */
export function useOptionalSpecialty(): SpecialtyContextValue | null {
  return useContext(SpecialtyContext);
}
